import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import { get, orderBy } from 'lodash';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildPopularStylesPath,
  FeatureSpecsEntities,
  FeatureSpecsPaths,
  VehicleEntities,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
import { buildSegmentRatingsPath, TypeRankingsFeaturesModel } from 'client/data/models/type-rankings-features';
import {
  buildAuthorPath,
  buildRatedByPath,
  EditorialReviewEntities,
  EditorialReviewModel,
} from 'client/data/models/editorial-review';
import { TypeRankingsEntities } from 'client/data/models/type-rankings-entities';
import { PageModel } from 'client/data/models/page';
import { MENU_HASH } from 'site-modules/core-page/utils/constants';
import { SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import {
  getAppraisalLink,
  getAppraisalLinkTextFromVehicle,
} from 'site-modules/shared/utils/appraisal-link-constructor';
import { stripHtml } from 'client/utils/string-utils';
import { CorePageParams, getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { getRatingScale } from 'site-modules/shared/utils/editorial-helper';
import { getBestSegmentRating } from 'site-modules/shared/utils/core-page/rating-and-ranking';
import { getCredibilityData, getLayoutProps } from 'site-modules/core-page/utils/core-highlights';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { TruncatedTextLite } from 'site-modules/shared/components/truncated-text-lite/truncated-text-lite';
import { RatingBadge } from 'site-modules/core-page/components/rating-badge/rating-badge';
import { Link } from 'site-modules/shared/components/link/link';
import { ArticleAuthor } from 'site-modules/shared/components/preprod-articles/article-author';
import { CardsRow } from './cards-row/cards-row';

import './core-highlights.scss';

const CREATIVE_ID = 'edmunds-highlights';

function CoreHighlightsUI({
  className,
  params,
  vehicle,
  styles,
  editorialReview,
  sharedTrimName,
  segmentRating,
  stylesFeatures,
  reviewAuthor,
  reviewRatedBy,
  isUsed,
  isMobile,
  noCardsRow,
  isCore7032Enabled,
}) {
  const ratings = editorialReview?.ratings;
  const isScorecardV4 = editorialReview?.isScorecardV4;
  const editorialRating = ratings?.overall?.rating;
  const editorialVerdict = ratings?.overall?.text;
  const ratingDisclaimer = editorialReview?.ratingDisclaimer;
  const ratingScale = getRatingScale(editorialReview);
  const typeRating = segmentRating?.rank;

  const currentAuthor = reviewRatedBy?.contentMetadata || reviewAuthor?.contentMetadata;

  if ((noCardsRow && !editorialVerdict) || (!noCardsRow && !(styles && stylesFeatures))) {
    return null;
  }

  const credibilityData = getCredibilityData({
    vehicleName: `${vehicle.make.name} ${vehicle.model.name}`,
    hasEditorialRating: !!editorialRating,
    hasLongTerm: !!editorialReview?.longTerm,
  });

  const cardsRow = !noCardsRow && (
    <Fragment>
      <CardsRow
        params={params}
        sharedTrimName={sharedTrimName}
        styles={styles}
        stylesFeatures={stylesFeatures}
        segmentRating={segmentRating}
        editorialVerdict={editorialVerdict}
        editorialRating={editorialRating}
        reviewEstimatedRange={editorialReview?.estimatedRange}
        ratingScale={ratingScale}
        creativeId={CREATIVE_ID}
        isScorecardV4={isScorecardV4}
        isUsed={isUsed}
        isCore7032Enabled={isCore7032Enabled}
      />
      {isUsed && (
        <div className="d-flex flex-column align-items-start mt-1">
          {!editorialVerdict && (
            <Button
              tag={ScrollLink}
              to={MENU_HASH.REVIEWS}
              scrollConfig={{ offset: SCROLL_OFFSET }}
              color="link"
              className="rounded medium mb-0_25 text-transform-none fw-normal text-cool-gray-40 px-0 py-0"
              data-tracking-id="view_edmunds_review"
            >
              Read full review
              <i className="icon-arrow-right3 text-primary-darker d-inline-block size-10 ms-0_25" aria-hidden />
            </Button>
          )}
          <Button
            tag={ScrollLink}
            to={MENU_HASH.FEATURES}
            scrollConfig={{ offset: SCROLL_OFFSET }}
            color="link"
            className="rounded medium text-transform-none fw-normal text-cool-gray-40 px-0 py-0"
            data-tracking-id="view_features_specs"
          >
            See all features & specs
            <i className="icon-arrow-right3 text-primary-darker d-inline-block size-10 ms-0_25" aria-hidden />
          </Button>
          {isMobile && (
            <Fragment>
              <Button
                tag={Link}
                to={getAppraisalLink(params)}
                data-tracking-id="tool_select_appraise_your_car"
                color="link"
                className="rounded medium mt-0_25 text-transform-none fw-normal text-cool-gray-40 px-0 py-0"
              >
                {getAppraisalLinkTextFromVehicle(vehicle)}
                <i className="icon-arrow-right3 text-primary-darker d-inline-block size-10 ms-0_25" aria-hidden />
              </Button>
              <Button
                tag={ScrollLink}
                to={MENU_HASH.INVENTORY}
                color="blue-50"
                size="lg"
                className="w-100 size-16 rounded-8 fw-normal text-transform-none py-0_5 mt-1"
                data-tracking-id="view_shop_module"
              >
                See All for Sale
              </Button>
            </Fragment>
          )}
        </div>
      )}
      {isCore7032Enabled && (
        <div className="d-flex flex-column align-items-center align-items-md-start mt-1">
          <Button
            tag={ScrollLink}
            to={MENU_HASH.FEATURES}
            scrollConfig={{ offset: SCROLL_OFFSET }}
            color="link"
            className="rounded medium text-transform-none fw-normal text-underline px-0 py-0"
            data-tracking-id="view_features_specs"
          >
            See features and specs
          </Button>
        </div>
      )}
    </Fragment>
  );

  const ratingDisclaimerFragment = !!ratingDisclaimer && (
    <Fragment>
      {!isMobile && <hr className="mt-1 mb-0_75" />}
      {/* To not be crawled */}
      <style>
        {`
          .no-crawl::before {
            content: attr(data-content);
          }
        `}
      </style>
      <em className="no-crawl small me-0_25" data-content={stripHtml(ratingDisclaimer)} />
    </Fragment>
  );

  const readFullReviewLink = !isUsed && (
    <Fragment>
      {isMobile && isCore7032Enabled ? (
        <em className="d-inline-block medium">
          <i className="icon-arrow-down8 small" aria-hidden /> Scroll to read more
        </em>
      ) : (
        <ScrollLink
          to={MENU_HASH.REVIEWS}
          scrollConfig={{ offset: SCROLL_OFFSET }}
          className="rounded size-16 text-underline"
          data-tracking-id="view_edmunds_review"
        >
          Read full review
        </ScrollLink>
      )}
    </Fragment>
  );

  const isFloatVerdict = isMobile && !!editorialRating && !typeRating;
  const { verdictMdCol, verdictMaxLength, ratingMdCol } = getLayoutProps({
    hasTypeRanking: !!typeRating,
    hasEditorialRating: !!editorialRating,
    isFloatVerdict,
    isMobile,
  });

  const truncatedVerdictProps = {
    className: classnames({ 'with-review-btn': !isUsed }),
    text: editorialVerdict,
    isHtmlText: true,
    additionalContent: [
      { content: readFullReviewLink },
      { content: ratingDisclaimerFragment, className: 'd-block mt-1 no-line-height' },
    ].filter(({ content }) => content),
    maxTextLength: 250,
    isInlineBtn: true,
    btnClassName: 'text-underline size-16',
    btnTextLess: 'less',
    btnTextMore: 'more',
    btnTextA11y: 'about this model',
    expandTrackingId: 'expand_verdict',
    expandTrackingValue: 'more',
  };

  return (
    <div className={classnames('core-highlights', className)} data-tracking-parent={CREATIVE_ID}>
      {!!(editorialVerdict || isCore7032Enabled) && (
        <div
          className={classnames('bg-white px-1 py-1', {
            'rounded-8': !isCore7032Enabled,
            'rounded-bottom-8': isCore7032Enabled,
            'mb-2': isCore7032Enabled && cardsRow,
          })}
        >
          {isCore7032Enabled && (
            <Fragment>
              <Row className="g-0_75 align-items-center">
                {!!currentAuthor && (
                  <Col xs={12} md={5}>
                    <ArticleAuthor
                      author={currentAuthor}
                      byline="Tested by"
                      className="small"
                      photoClassName="rounded-8 me-0_5"
                      imageHeight={56}
                      imageWidth={62}
                      showTitle
                      lazyLoadImage
                      showBioTooltip
                    />
                  </Col>
                )}
                <Col xs={12} md={currentAuthor ? 7 : undefined} className="small">
                  <strong className="d-block size-16">
                    <span className="size-24">{credibilityData.dataPoint}</span> {credibilityData.text}
                  </strong>
                  <strong>We drove it.</strong> {credibilityData.droveItText}
                </Col>
              </Row>
              {!!editorialVerdict && (
                <Fragment>
                  <hr className="mt-0_75 mb-0_75 mb-md-1_25" />
                  <Row className="g-0_75 align-items-center">
                    {!!editorialRating && (
                      <Col
                        xs={12}
                        md={ratingMdCol}
                        className={classnames({ 'd-flex justify-content-center align-items-center': !isFloatVerdict })}
                      >
                        <div
                          className={classnames({
                            'border-right pe-0_75': typeRating,
                            'float-start me-1': isFloatVerdict,
                          })}
                        >
                          <RatingBadge
                            className="float-start float-md-none clickable"
                            ratingScale={ratingScale}
                            editorialRating={editorialRating}
                            isScorecardV4={isScorecardV4}
                            isScrollLink
                            isCore7032Enabled
                          />
                        </div>
                        {!!typeRating && (
                          <ScrollLink
                            className="fw-bold text-center flex-fill px-0_25 rounded-8 clickable"
                            to={MENU_HASH.COMPETITION}
                            scrollConfig={{ offset: SCROLL_OFFSET }}
                            data-tracking-id="view_ranking_compare"
                          >
                            Rank
                            <br />
                            <span className="size-24 interaction-underline">#{typeRating}</span>
                            <div className="d-flex flex-column small fw-normal">
                              <span className="text-underline">
                                out of{' '}
                                {segmentRating.editorialSegment.segmentRatings.filter(({ rating }) => rating).length}
                              </span>
                              <span>{get(segmentRating.editorialSegment, 'displayName', '')}</span>
                            </div>
                          </ScrollLink>
                        )}
                        {isFloatVerdict && <TruncatedTextLite {...truncatedVerdictProps} />}
                      </Col>
                    )}
                    {!isFloatVerdict && (
                      <Col xs={12} md={verdictMdCol}>
                        <TruncatedTextLite {...truncatedVerdictProps} maxTextLength={verdictMaxLength} />
                      </Col>
                    )}
                  </Row>
                </Fragment>
              )}
            </Fragment>
          )}
          {!isCore7032Enabled && (
            <Fragment>
              <div className={classnames('d-md-flex align-items-md-center clearfix', { 'mb-1': !noCardsRow })}>
                <RatingBadge
                  className="float-start float-md-none clickable px-md-0_75 me-1"
                  ratingScale={ratingScale}
                  editorialRating={editorialRating}
                  isScorecardV4={isScorecardV4}
                  isScrollLink
                />
                <div>
                  {isMobile ? (
                    <TruncatedTextLite {...truncatedVerdictProps} />
                  ) : (
                    <Fragment>
                      <ContentFragment componentToUse="span" classes="me-0_25">
                        {editorialVerdict}
                      </ContentFragment>
                      {readFullReviewLink}
                      {!!ratingDisclaimerFragment && <div className="no-line-height">{ratingDisclaimerFragment}</div>}
                    </Fragment>
                  )}
                  {isUsed && (
                    <div className="mt-0_75">
                      <Button
                        tag={ScrollLink}
                        to={MENU_HASH.REVIEWS}
                        scrollConfig={{ offset: SCROLL_OFFSET }}
                        color="outline-primary-b"
                        className="rounded-8 size-16 px-1 py-0_25"
                        data-tracking-id="view_edmunds_review"
                      >
                        Read full review
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              {cardsRow}
            </Fragment>
          )}
        </div>
      )}
      {(!editorialVerdict || isCore7032Enabled) && cardsRow}
    </div>
  );
}

CoreHighlightsUI.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  className: PropTypes.string,
  styles: VehicleEntities.PopularStyles,
  sharedTrimName: PropTypes.string,
  editorialReview: EditorialReviewEntities.EditorialReview,
  segmentRating: TypeRankingsEntities.SegmentRating,
  stylesFeatures: PropTypes.arrayOf(FeatureSpecsEntities.FeatureStyleEntity),
  reviewAuthor: EditorialReviewEntities.Author,
  reviewRatedBy: EditorialReviewEntities.Author,
  isUsed: PropTypes.bool,
  noCardsRow: PropTypes.bool,
  isMobile: PropTypes.bool,
  isCore7032Enabled: PropTypes.bool,
  // used in getBestSegmentRating. Added here to avoid accidental removal
  submodelSlug: PropTypes.string,
};

CoreHighlightsUI.defaultProps = {
  className: null,
  styles: null,
  sharedTrimName: null,
  editorialReview: null,
  segmentRating: null,
  stylesFeatures: null,
  reviewAuthor: null,
  reviewRatedBy: null,
  isUsed: false,
  noCardsRow: false,
  isMobile: false,
  isCore7032Enabled: null,
  submodelSlug: null,
};

export const CoreHighlights = connectToModel(CoreHighlightsUI, {
  sharedTrimName: bindToPath('sharedValues.trimName', PageModel),
  stylesFeatures: bindToPath(
    ({ vehicle, isPreprod }) => !isPreprod && FeatureSpecsPaths.buildPartialFeaturesPath(getParamsFromVehicle(vehicle)),
    VehicleModel
  ),
  styles: bindToPath(
    ({ vehicle, isPreprod }) => !isPreprod && buildPopularStylesPath(getParamsFromVehicle(vehicle)),
    VehicleModel,
    popularStyles => orderBy(popularStyles, 'price.baseMSRP')
  ),
  segmentRating: bindToPath(
    ({ params: { make, model, year }, isUsed, isPreprod }) =>
      !isUsed && !isPreprod && buildSegmentRatingsPath({ makeSlug: make, modelSlug: model, year }),
    TypeRankingsFeaturesModel,
    getBestSegmentRating
  ),
  // TODO: Review after core-7032
  reviewAuthor: bindToPath(
    ({ vehicle, isCore7032Enabled }) => isCore7032Enabled && buildAuthorPath(getParamsFromVehicle(vehicle)),
    EditorialReviewModel
  ),
  reviewRatedBy: bindToPath(
    ({ vehicle, isCore7032Enabled }) => isCore7032Enabled && buildRatedByPath(getParamsFromVehicle(vehicle)),
    EditorialReviewModel
  ),
});
